import { Button, useToast } from '@chakra-ui/react';
import { Workshop } from 'api/types';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  manufacturer: string;
  accountData: Workshop & {
    username: string;
    password: string;
    countryName: string;
    languageName: string;
    vatWithoutPrefix: string;
    locale: `${string}_${string}`;
    customerIdWithoutPrefix: string;
  };
};

export const CopyAccountDataAsJsonButton = ({ manufacturer, accountData }: Props) => {
  const { t } = useTranslation();
  const toast = useToast({ ...defaultToastOptions });

  const manufacturerKey = useMemo<Manufacturer | null>(() => {
    if (manufacturer.toLowerCase().includes('bmw')) {
      return 'bmw';
    }
    if (manufacturer.toLowerCase().includes('erwin')) {
      return 'erwin';
    }
    if (manufacturer.toLowerCase().includes('topix')) {
      return 'topix';
    }
    if (manufacturer.toLowerCase().includes('rmi - mg')) {
      return 'mg';
    }
    if (manufacturer.toLowerCase().includes('honda')) {
      return 'honda';
    }
    if (manufacturer.toLowerCase().includes('ford')) {
      return 'ford';
    }
    if (manufacturer.toLowerCase().includes('mazda')) {
      return 'mazda';
    }
    return null;
  }, [manufacturer]);

  const dataToCopy = useMemo(() => {
    let data: Partial<Record<ExportField, string | number | null>> = {};
    if (manufacturerKey == null) {
      return data;
    }
    Object.entries(fieldMapping[manufacturerKey]).forEach(([exportKey, importKey]) => {
      data[exportKey as ExportField] = accountData[importKey] ?? null;
    });
    return data;
  }, [accountData, manufacturerKey]);

  if (manufacturerKey == null) {
    return null;
  }

  return (
    <Button
      colorScheme="purple"
      onClick={() => {
        navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
        toast({
          title: t('account:copy_account_data_as_json.toast.success.title'),
          status: 'success',
        });
      }}
    >
      {t('account:copy_account_data_as_json.cta')}
    </Button>
  );
};

type ExportField =
  | 'firstName'
  | 'lastName'
  | 'company'
  | 'street'
  | 'city'
  | 'postalCode'
  | 'country'
  | 'username'
  | 'password'
  | 'vatNumber'
  | 'language'
  | 'customerId'
  | 'privateEmail'
  | 'privatePhone';

/**
 * Mapping from our internal account data fields to the fields expected by each manufacturer.
 */
const fieldMapping = {
  bmw: {
    firstName: 'firstname',
    lastName: 'lastname',
    company: 'name',
    street: 'address',
    city: 'city',
    postalCode: 'zipcode',
    country: 'countryName',
    username: 'username',
    vatNumber: 'vat_number',
    language: 'languageName',
  },
  erwin: {
    firstName: 'firstname',
    lastName: 'lastname',
    company: 'name',
    street: 'address',
    city: 'city',
    postalCode: 'zipcode',
    country: 'countryName',
    username: 'username',
  },
  topix: {
    firstName: 'firstname',
    lastName: 'lastname',
    company: 'name',
    street: 'address',
    city: 'city',
    postalCode: 'zipcode',
    country: 'country',
    username: 'username',
    password: 'password',
    language: 'locale',
  },
  mg: {
    firstName: 'firstname',
    lastName: 'lastname',
    company: 'name',
    street: 'address',
    city: 'city',
    postalCode: 'zipcode',
    country: 'countryName',
    username: 'username',
    password: 'password',
    vatNumber: 'vat_number',
  },
  honda: {
    firstName: 'firstname',
    lastName: 'lastname',
    company: 'name',
    street: 'address',
    city: 'city',
    postalCode: 'zipcode',
    country: 'countryName',
    username: 'username',
    language: 'languageName',
  },
  ford: {
    firstName: 'firstname',
    lastName: 'lastname',
    company: 'name',
    street: 'address',
    city: 'city',
    postalCode: 'zipcode',
    country: 'countryName',
    username: 'username',
    password: 'password',
    language: 'languageName',
    privateEmail: 'email',
    privatePhone: 'telephone',
  },
  mazda: {
    firstName: 'firstname',
    lastName: 'lastname',
    company: 'name',
    street: 'address',
    city: 'city',
    postalCode: 'zipcode',
    country: 'country',
    vatNumber: 'vatWithoutPrefix',
    customerId: 'customerIdWithoutPrefix',
  },
} as const satisfies Record<string, Partial<Record<ExportField, keyof Props['accountData']>>>;

type Manufacturer = keyof typeof fieldMapping;
